/**
 * SidebarShortcuts — "ACCÈS RAPIDE" section between main nav and Appli+.
 * Shows the 3 most recently visited routes (excluding the current one),
 * plus a "…" button that opens a popover with up to 20 entries.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  Dumbbell,
  BookOpen,
  Users,
  Calendar,
  Heart,
  UserCircle,
  Trophy,
  LayoutGrid,
  CreditCard,
  Compass,
  type LucideIcon,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useNavigationHistory,
  type NavHistoryEntry,
} from "@/hooks/useNavigationHistory";

interface RouteMeta {
  label: string;
  icon: LucideIcon;
}

const ROUTE_LABELS: Record<string, RouteMeta> = {
  "/dashboard": { label: "Accueil", icon: Home },
  "/programs": { label: "Programmes", icon: Dumbbell },
  "/library": { label: "Bibliothèque", icon: BookOpen },
  "/community": { label: "Communauté", icon: Users },
  "/planning": { label: "Planning", icon: Calendar },
  "/wellness": { label: "Bien-être", icon: Heart },
  "/athlete-profile": { label: "Profil", icon: UserCircle },
  "/achievements": { label: "Succès", icon: Trophy },
  "/modules": { label: "Modules", icon: LayoutGrid },
  "/billing": { label: "Abonnement", icon: CreditCard },
};

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function resolveMeta(pathname: string): RouteMeta {
  // Exact match first
  const exact = ROUTE_LABELS[pathname];
  if (exact) return exact;

  // Prefix match (e.g. /programs/123 → /programs)
  const segments = pathname.split("/").filter(Boolean);
  for (let i = segments.length; i > 0; i--) {
    const candidate = "/" + segments.slice(0, i).join("/");
    const match = ROUTE_LABELS[candidate];
    if (match) return match;
  }

  // Fallback: capitalized last segment
  const last = segments[segments.length - 1] ?? "";
  return {
    label: capitalize(last.replace(/-/g, " ")) || "Page",
    icon: Compass,
  };
}

function HistoryRow({
  entry,
  onNavigate,
}: {
  entry: NavHistoryEntry;
  onNavigate: (path: string) => void;
}) {
  const meta = resolveMeta(entry.pathname);
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={() => onNavigate(entry.pathname)}
      className="w-full h-7 flex items-center gap-2 px-3 rounded-md text-xs text-foreground hover:bg-sidebar-accent transition-colors text-left"
      title={entry.pathname}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{meta.label}</span>
    </button>
  );
}

export function SidebarShortcuts() {
  const navigate = useNavigate();
  const { history, currentPath } = useNavigationHistory();
  const [open, setOpen] = useState(false);

  // Routes already in main nav or reachable via logo — exclude from shortcuts
  const MAIN_NAV_PATHS = new Set(["/dashboard", "/programs", "/community", "/library", "/features", "/home"]);

  const filtered = useMemo(
    () => history.filter((e) => e.pathname !== currentPath && !MAIN_NAV_PATHS.has(e.pathname)),
    [history, currentPath]
  );

  if (filtered.length === 0) return null;

  const top3 = filtered.slice(0, 3);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div className="px-1 pt-1 pb-2">
      <p className="px-3 py-1 text-xs text-muted-foreground/60 uppercase tracking-wider">
        Accès rapide
      </p>
      <div className="space-y-0.5">
        {top3.map((entry) => (
          <HistoryRow
            key={entry.pathname}
            entry={entry}
            onNavigate={go}
          />
        ))}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-full h-6 flex items-center justify-center rounded-md text-base leading-none text-muted-foreground hover:bg-sidebar-accent hover:text-foreground transition-colors"
              title="Voir tout l'historique"
              aria-label="Voir tout l'historique"
            >
              …
            </button>
          </PopoverTrigger>
            <PopoverContent
              side="right"
              align="start"
              className="w-60 p-1"
            >
              <p className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                Historique récent
              </p>
              <div className="space-y-0.5 max-h-80 overflow-y-auto">
                {filtered.slice(0, 20).map((entry) => (
                  <HistoryRow
                    key={entry.pathname}
                    entry={entry}
                    onNavigate={go}
                  />
                ))}
              </div>
            </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
